import React, { Fragment, useEffect, useRef, useState } from "react";
import GptLayout from "../gptLayout";
import videoChest1 from "../../images/gdVideos/chest1Combine.mp4";
import videoChest2 from "../../images/gdVideos/chest2Combine.mp4";
import videoChest3 from "../../images/gdVideos/chest3Combine.mp4";
import videoChest4 from "../../images/gdVideos/chest4Combine.mp4";
import videoAlpha from "../../images/gdVideos/Alpha.mp4";

import "../../styles/groupDiscuss.scss";
import { HiMiniSpeakerWave } from "react-icons/hi2";

// Assume each video contains both speaking and non-speaking segments
const gdArray = [
  {
    id: 1,
    videoSrc: videoChest1,
    title: "Chest No. 1",
    speaking: false,
    speakingRange: { start: 0, end: 4 },
    idleRange: { start: 5, end: 10 },
  },
  {
    id: 2,
    videoSrc: videoChest2,
    title: "Chest No. 2",
    speaking: false,
    speakingRange: { start: 0, end: 4 },
    idleRange: { start: 5, end: 10 },
  },
  {
    id: 3,
    videoSrc: videoChest3,
    title: "Chest No. 3",
    speaking: false,
    speakingRange: { start: 0, end: 4 },
    idleRange: { start: 5, end: 10 },
  },
  {
    id: 4,
    videoSrc: videoChest4,
    title: "Chest No. 4",
    speaking: false,
    speakingRange: { start: 0, end: 4 },
    idleRange: { start: 5, end: 10 },
  },
  {
    id: 5,
    videoSrc: videoAlpha,
    title: "Capt. Alpha",
    speaking: false,
    speakingRange: { start: 0, end: 4 },
    idleRange: { start: 5, end: 10 },
  },
];

const GroupDiscussion = () => {
  const [participants, setParticipants] = useState(gdArray);
  const videoRefs = useRef(new Map());
  const cameraRef = useRef(null);
  const streamRef = useRef(null);
  const [searchValue, setSearchValue] = useState("");
  const recognitionRef = useRef(null);
  const [userIsSpeaking, setUserIsSpeaking] = useState(false);

  const startSpeechRecognition = () => {
    if ("webkitSpeechRecognition" in window) {
      const recognition = new window.webkitSpeechRecognition();
      recognition.lang = "en-US";
      recognition.continuous = true;

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map((result) => result[0].transcript)
          .join("");
        setSearchValue(transcript);
        setUserIsSpeaking(true);

        // Set Captain Alpha to idle when user is speaking
        setParticipants((prev) =>
          prev.map((p) => (p.id === 5 ? { ...p, speaking: false } : p))
        );
      };

      recognition.onspeechend = () => {
        setUserIsSpeaking(false);
      };

      recognition.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setUserIsSpeaking(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } else {
      console.warn("Speech Recognition not supported in this browser.");
    }
  };

  const stopSpeechRecognition = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
      setUserIsSpeaking(false);
    }
  };

  const startCameraAndMic = () => {
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: true })
      .then((stream) => {
        streamRef.current = stream;
        if (cameraRef.current) {
          cameraRef.current.srcObject = stream;
        }
      })
      .catch((err) => {
        console.error("Error accessing the camera and microphone:", err);
      });
    startSpeechRecognition();
  };

  const stopCameraAndMic = () => {
    if (streamRef.current) {
      const tracks = streamRef.current.getTracks();
      tracks.forEach((track) => track.stop());
      streamRef.current = null;
      if (cameraRef.current) {
        cameraRef.current.srcObject = null;
      }
    }
    stopSpeechRecognition();
  };

  // Logic for the video playback based on speaking state
  useEffect(() => {
    participants.forEach((participant) => {
      const videoRef = videoRefs.current.get(participant.id);
      if (!videoRef) return;

      // Only Captain Alpha (id: 5) can be in speaking state, others always idle
      const range =
        participant.id === 5 && participant.speaking
          ? participant.speakingRange
          : participant.idleRange;

      videoRef.currentTime = range.start;

      const playVideo = () => {
        if (videoRef.readyState >= 2) {
          const playPromise = videoRef.play();
          if (playPromise !== undefined) {
            playPromise.catch((error) => {
              console.log("Playback error handled:", error);
            });
          }
        } else {
          videoRef.addEventListener(
            "loadeddata",
            () => {
              const playPromise = videoRef.play();
              if (playPromise !== undefined) {
                playPromise.catch((error) => {
                  console.log("Playback error handled after load:", error);
                });
              }
            },
            { once: true }
          );
        }
      };

      playVideo();

      const handleTimeUpdate = () => {
        // Only Captain Alpha (id: 5) can be in speaking state, others always idle
        const currentRange =
          participant.id === 5 && participant.speaking
            ? participant.speakingRange
            : participant.idleRange;

        if (videoRef.currentTime >= currentRange.end) {
          videoRef.currentTime = currentRange.start;
        }
      };

      videoRef.addEventListener("timeupdate", handleTimeUpdate);
      return () => {
        videoRef.removeEventListener("timeupdate", handleTimeUpdate);
      };
    });
  }, [participants]);

  // Initial setup
  useEffect(() => {
    startCameraAndMic();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopCameraAndMic();
      } else {
        startCameraAndMic();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Only simulate Captain Alpha speaking randomly
    // const speakingInterval = setInterval(() => {
    //   if (!userIsSpeaking) {
    //     setParticipants((prevParticipants) => {
    //       return prevParticipants.map((participant) => {
    //         // Only Captain Alpha (id: 5) can change speaking state
    //         if (participant.id === 5) {
    //           const shouldSpeak = Math.random() > 0.5;
    //           return {
    //             ...participant,
    //             speaking: shouldSpeak,
    //           };
    //         }
    //         // All other participants are always in idle state
    //         return {
    //           ...participant,
    //           speaking: false,
    //         };
    //       });
    //     });
    //   }
    // }, 5000);

    return () => {
      stopCameraAndMic();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // clearInterval(speakingInterval);
    };
  }, [userIsSpeaking]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && searchValue !== "") {
      uploadUserAnswer();
    }
  };

  const uploadUserAnswer = () => {
    console.log(searchValue);
    setParticipants((prev) =>
      prev.map((p) => (p.id === 5 ? { ...p, speaking: true } : p))
    );
  };

  const setVideoRef = (id, element) => {
    if (element) {
      videoRefs.current.set(id, element);
    }
  };

  return (
    <Fragment>
      <GptLayout>
        <section className="gd_disscuss">
          <div className="container dis_wrap">
            <div className="title">
              <h2>Group Discussion</h2>
            </div>
            <div className="attempt_test">
              <div className="video_test_card">
                {participants.map((participant) => (
                  <div key={participant.id} className="gd_video">
                    <p>{participant.title}</p>
                    {participant.speaking && (
                      <span>
                        <HiMiniSpeakerWave />
                      </span>
                    )}
                    <video
                      ref={(el) => setVideoRef(participant.id, el)}
                      playsInline
                      webkit-playsinline="true"
                      muted
                    >
                      <source src={participant.videoSrc} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                ))}
                <div className="gd_video">
                  <p>Chest No.5</p>
                  {userIsSpeaking && (
                    <span>
                      <HiMiniSpeakerWave />
                    </span>
                  )}
                  <video ref={cameraRef} autoPlay playsInline />
                </div>
              </div>

              <div className="input_box">
                <textarea
                  type="text"
                  value={searchValue}
                  style={{ border: "1px solid #fff" }}
                  onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Write your answer"
                />
                <div className="btn_group">
                  <button className="submit" onClick={uploadUserAnswer}>
                    Submit
                  </button>
                  <button className="submit" onClick={startSpeechRecognition}>
                    Refresh Mic
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </GptLayout>
    </Fragment>
  );
};

export default GroupDiscussion;
